/* eslint-disable consistent-return */

import { useState, useEffect, useRef, useCallback } from 'react'
import { ApiResponse } from 'src/types/api.types'
import type { DynamicPricingResponse } from '@query/page-query/pricing-query/pricing.model'
import type { ConsolidatedDynamicPricingData } from 'src/app/api/v1/pricing/dynamic-pricing-all/route'

type ConsolidatedDynamicPricingOptions = {
  /** Debounce delay in milliseconds (default: 500ms) */
  debounceMs?: number
  /** Initial annual spend value */
  initialSpend?: number
  /** Callback on successful fetch */
  onSuccess?: (data: ConsolidatedDynamicPricingData) => void
  /** Callback on error */
  onError?: (error: Error) => void
  /** Amount limit - above this will return enterprise data */
  amountLimit?: number
}

/**
 * Enterprise pricing fallback response
 */
const createEnterprisePricing = (): DynamicPricingResponse => ({
  plan_name: 'Enterprise',
  charge_rate: 0,
  price_usd: 0,
  label_price_usd: 0,
  discount: 0,
  trial_period: 0,
  projected_net_savings: 0,
  plan_type: 'ENTERPRISE',
  max_annual_spend: 0,
  plan_id: '',
  cloud_spend_range_id: '',
  monthly_price_for_annual_plan: 0,
  comparable_plans: {},
  projected_net_saving_rate: 0,
  credits: 0,
  price: 0,
  currency: 'USD',
  label_price: 0,
})

/**
 * Fetches consolidated dynamic pricing from the Next.js API route
 */
async function fetchConsolidatedDynamicPricingFromApi(
  maxAnnualSpend: number
): Promise<ConsolidatedDynamicPricingData> {
  const params = new URLSearchParams({
    max_annual_spend: maxAnnualSpend.toString(),
  })

  const response = await fetch(`/api/v1/pricing/dynamic-pricing-all?${params}`)
  const json: ApiResponse<ConsolidatedDynamicPricingData> =
    await response.json()

  if (!json.success || !json.data) {
    throw new Error(
      json.error || 'Failed to fetch consolidated dynamic pricing'
    )
  }

  return json.data
}

/**
 * Hook to fetch consolidated dynamic pricing data for all 4 combinations
 * (USD/INR × ANNUAL/MONTHLY) in a single API call.
 *
 * This replaces the previous approach of using multiple useDualCurrencyPricing hooks
 * and reduces 4 API calls to just 1.
 *
 * @example
 * ```tsx
 * const {
 *   pricingUSDAnnual,
 *   pricingUSDMonthly,
 *   pricingINRAnnual,
 *   pricingINRMonthly,
 *   annualSpend,
 *   setAnnualSpend,
 *   isLoading
 * } = useConsolidatedDynamicPricing({
 *   initialSpend: 20000,
 *   debounceMs: 500,
 *   amountLimit: 500000,
 * })
 * ```
 */
export const useConsolidatedDynamicPricing = ({
  debounceMs = 500,
  initialSpend = 200000,
  onSuccess,
  onError,
  amountLimit,
}: ConsolidatedDynamicPricingOptions = {}) => {
  const [annualSpend, setAnnualSpend] = useState<string>(
    initialSpend.toString()
  )
  const [pricingUSDAnnual, setPricingUSDAnnual] =
    useState<DynamicPricingResponse | null>(null)
  const [pricingUSDMonthly, setPricingUSDMonthly] =
    useState<DynamicPricingResponse | null>(null)
  const [pricingINRAnnual, setPricingINRAnnual] =
    useState<DynamicPricingResponse | null>(null)
  const [pricingINRMonthly, setPricingINRMonthly] =
    useState<DynamicPricingResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Refs for tracking state
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isInitialFetchRef = useRef<boolean>(true)
  const lastFetchedSpendRef = useRef<number | null>(null)

  // Fetch function
  const fetchPricing = useCallback(
    async (spend: number) => {
      // Skip if we already fetched this exact spend value
      if (lastFetchedSpendRef.current === spend) {
        return
      }

      // Don't fetch if spend is 0 or invalid - fetch with 1 as minimum
      const effectiveSpend = !spend || spend <= 0 ? 1 : spend

      // If spend exceeds limit, set enterprise pricing for all
      if (effectiveSpend && amountLimit && effectiveSpend > amountLimit) {
        const enterprisePricing = createEnterprisePricing()
        setPricingUSDAnnual(enterprisePricing)
        setPricingUSDMonthly(enterprisePricing)
        setPricingINRAnnual({ ...enterprisePricing, currency: 'INR' })
        setPricingINRMonthly({ ...enterprisePricing, currency: 'INR' })
        lastFetchedSpendRef.current = effectiveSpend
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data =
          await fetchConsolidatedDynamicPricingFromApi(effectiveSpend)
        setPricingUSDAnnual(data.usdAnnual)
        setPricingUSDMonthly(data.usdMonthly)
        setPricingINRAnnual(data.inrAnnual)
        setPricingINRMonthly(data.inrMonthly)
        lastFetchedSpendRef.current = effectiveSpend
        onSuccess?.(data)
      } catch (err) {
        if (err instanceof Error) {
          setError(err)
          onError?.(err)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [amountLimit, onSuccess, onError]
  )

  const convertedAnnualSpend = Number(annualSpend)

  // Single effect for both initial and subsequent fetches
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // For initial fetch, don't debounce - fetch immediately
    if (isInitialFetchRef.current) {
      isInitialFetchRef.current = false
      void fetchPricing(convertedAnnualSpend)
      return
    }

    // For subsequent fetches, use debounce
    debounceTimerRef.current = setTimeout(() => {
      void fetchPricing(convertedAnnualSpend)
    }, debounceMs)

    // Cleanup on unmount or value change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [convertedAnnualSpend, debounceMs, fetchPricing])

  return {
    /** USD Annual pricing data */
    pricingUSDAnnual,
    /** USD Monthly pricing data */
    pricingUSDMonthly,
    /** INR Annual pricing data */
    pricingINRAnnual,
    /** INR Monthly pricing data */
    pricingINRMonthly,
    /** Whether a fetch is in progress */
    isLoading,
    /** Any error that occurred */
    error,
    /** Current annual spend value */
    annualSpend,
    /** Function to update the annual spend (triggers debounced fetch) */
    setAnnualSpend,
    /** Force refetch with current spend */
    refetch: () => {
      lastFetchedSpendRef.current = null // Reset to allow refetch
      return fetchPricing(convertedAnnualSpend)
    },
  }
}
